import React from "react";
import OpenLinkInNewTab from "shared/form/core/link";
import FieldRenderType, {
  HideEmptyValueInDisplayProfile,
  MultipleResourceObjectsForDisplayProfile,
  MultipleValuesForDisplayProfile,
  SingleResourceObjectForDisplayProfile
} from "shared/form/renderer/enums/FieldRenderType";
import { ApiFieldForProfileDisplay, OptionValueWithResource } from "shared/types/ApiFieldForProfileDisplay";
import { Copies, useCopies } from "shared/hooks/useCopies";
import { getFormattedNumberOrCurrency, removeHtmlTagsFromString } from "shared/utils";
import FormGroup from "shared/form/fields/formGroup";
import FormLabel from "shared/form/fields/formLabel";
import { PreviewFile, PreviewImage } from "shared/form/core/assetUpload/defaultAssetPreview";
import PreviewMultiImageSlider from "shared/components/imageSlider";
import { useRegDetail } from "shared/context/regDetail";
import CurrencyCode from "shared/enums/CurrencyCode";
import { ApiResourceAsset } from "shared/types/ApiResourceAsset";
import { AssetPreviewType } from "shared/components/imageSlider/type";
import DisplayIcon from "shared/form/core/displayIcon";
import EmailLinkClient from "shared/components/emailLink";
import { DEFAULT_VALUE_FOR_EMPTY_FIELD } from "shared/utils/constants";
import classNames from "classnames";
import sortBy from "lodash/sortBy";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import { ImageAspectRatio, ImageAspectRatioToDimension } from "shared/form/core/assetUpload/enum";

export default function ViewProfileReadOnlyField(props: { field: ApiFieldForProfileDisplay }) {
  return (
    <FormGroup fullWidth={props.field.fullWidthView} indented={props.field.indented}>
      <FieldDisplayLabel field={props.field} />
      <FieldDisplayValue field={props.field} />
    </FormGroup>
  );
}

function FieldDisplayLabel(props: { field: ApiFieldForProfileDisplay }) {
  if (!props.field.displayLabel || props.field.renderType === FieldRenderType.SECTION_SEPARATOR) {
    return null;
  }

  if (props.field.renderType === FieldRenderType.HTML) {
    return (
      <div className="form_label">
        {props.field.imageIcon && <DisplayIcon iconUrl={props.field.imageIcon.url} />}
        <span
          title={removeHtmlTagsFromString(props.field.displayLabel)}
          dangerouslySetInnerHTML={{ __html: props.field.displayLabel }}
        />
      </div>
    );
  }

  return (
    <FormLabel
      label={props.field.displayLabel}
      iconUrl={props.field.imageIcon?.url}
      addFormLabelClass={!Boolean(props.field.renderType === FieldRenderType.CHECK_BOX && props.field.imageIcon)}
    />
  );
}

function FieldDisplayValue(props: { field: ApiFieldForProfileDisplay }) {
  const copies = useCopies();
  const { localeId, currencyCode } = useRegDetail();

  const valuesObj = React.useMemo(() => {
    if (props.field.renderType === FieldRenderType.TAGS) {
      return { strValuesArr: props.field.value };
    } else if (MultipleValuesForDisplayProfile.includes(props.field.renderType)) {
      return { valuesArr: props.field.value };
    } else if (MultipleResourceObjectsForDisplayProfile.includes(props.field.renderType)) {
      const obj: Array<ApiResourceAsset> | undefined =
        typeof props.field.value === "object" && !Array.isArray(props.field.value)
          ? (props.field.value.value as Array<ApiResourceAsset>)
          : undefined;
      return { resourceObjArr: obj };
    } else if (SingleResourceObjectForDisplayProfile.includes(props.field.renderType)) {
      return {
        resourceObj:
          typeof props.field.value === "object" && !Array.isArray(props.field.value)
            ? (props.field.value.value as ApiResourceAsset)
            : undefined
      };
    } else {
      return { value: props.field.value };
    }
  }, [props.field.renderType, props.field.value]);

  if (props.field.renderType === FieldRenderType.CHECK_BOX && props.field.imageIcon) {
    return null;
  }

  return (
    <div
      className={classNames("form_value", {
        "singleImg--field": valuesObj.resourceObj
      })}>
      {getValueByRenderTypeForDisplayProfile({
        renderType: props.field.renderType,
        copies,
        localeId,
        currencyCode,
        requiresFormatting: props.field.requiresFormatting,
        value: valuesObj.value as OptionValueWithResource,
        valuesArr: valuesObj.valuesArr as Array<OptionValueWithResource>,
        strValuesArr: valuesObj.strValuesArr as OptionValueWithResource,
        resourceObj: valuesObj.resourceObj as ApiResourceAsset,
        resourceObjArr: valuesObj.resourceObjArr as Array<ApiResourceAsset>,
        imageAspectRatio: props.field.imageAspectRatio
      })}
    </div>
  );
}

function getValueByRenderTypeForDisplayProfile(args: {
  renderType: FieldRenderType;
  copies: Copies;
  localeId: string;
  currencyCode: CurrencyCode;
  requiresFormatting: boolean;
  value?: OptionValueWithResource;
  valuesArr?: Array<OptionValueWithResource>;
  strValuesArr?: OptionValueWithResource;
  resourceObj?: ApiResourceAsset;
  resourceObjArr?: Array<ApiResourceAsset>;
  imageAspectRatio?: ImageAspectRatio;
}) {
  // no values available for display for render types apart from HTML and Section_Separator
  if (
    (args.value?.value === null || args.value?.value === undefined) &&
    !args.valuesArr?.length &&
    !args.resourceObj &&
    !args.strValuesArr &&
    !args.resourceObjArr?.length &&
    !HideEmptyValueInDisplayProfile.includes(args.renderType)
  ) {
    return DEFAULT_VALUE_FOR_EMPTY_FIELD;
  }
  switch (args.renderType) {
    case FieldRenderType.CHECK_BOX:
      return args.value?.value ? args.copies.get("yes") : args.copies.get("no");
    case FieldRenderType.DATE:
      return DateTimeHelper.formatDate({ startDate: args.value?.value as string });
    case FieldRenderType.DATE_TIME:
      return DateTimeHelper.formatDate({
        startDate: args.value?.value as string,
        option: {
          showTime: true
        }
      });
    case FieldRenderType.INTEGER:
      return args.requiresFormatting
        ? getFormattedNumberOrCurrency({
            value: args.value?.value as string,
            formatValue: true,
            localeId: args.localeId
          })
        : args.value?.value;
    case FieldRenderType.MONEY:
      return args.requiresFormatting
        ? getFormattedNumberOrCurrency({
            value: args.value?.value as string,
            formatValue: true,
            localeId: args.localeId,
            currencyCode: args.currencyCode
          })
        : args.value?.value;
    case FieldRenderType.MULTI_FILE:
    case FieldRenderType.PRIVATE_MULTI_FILE:
      return (
        <div className="defaultFileAsset--wrapper">
          {args.resourceObjArr?.map((o: ApiResourceAsset) => (
            <div className="defaultAsset--fileItem">
              <PreviewFile key={o.id} {...o} />
            </div>
          ))}
        </div>
      );
    case FieldRenderType.MULTI_IMAGE:
      return <PreviewMultiImageSlider uploadedAssets={args.resourceObjArr as Array<AssetPreviewType>} />;
    case FieldRenderType.MULTI_SELECT:
    case FieldRenderType.NESTED_MULTI_SELECT:
      return renderOptionsWithIcons(args.valuesArr);
    case FieldRenderType.RADIO:
    case FieldRenderType.SINGLE_SELECT:
      return args.value && <RenderOptionWithIcon option={args.value} />;
    case FieldRenderType.MULTI_LINE_TEXTFIELD:
    case FieldRenderType.SINGLE_LINE_TEXTFIELD:
      return args.value?.value;
    case FieldRenderType.TAGS:
      return (
        Array.isArray(args.strValuesArr?.value) && (
          <div className="tagField--tagsBlock">
            {sortBy(args.strValuesArr?.value, (o: string) => o.toLowerCase())?.map(o => (
              <span className="tagField--tagsBlock_tag" key={o as string}>
                {o}
              </span>
            ))}
          </div>
        )
      );
    case FieldRenderType.SINGLE_IMAGE:
    case FieldRenderType.IMAGE:
      return <PreviewImage url={args.resourceObj?.url as string} />;
    case FieldRenderType.SINGLE_IMAGE_WITH_ASPECT_RATIO:
      return (
        <PreviewImage
          url={args.resourceObj?.url as string}
          dimension={args.imageAspectRatio ? ImageAspectRatioToDimension[args.imageAspectRatio]?.dimension : undefined}
        />
      );
    case FieldRenderType.URL:
      return <OpenLinkInNewTab url={args.value?.value as string} title={args.value?.value as string} />;
    case FieldRenderType.SOCIAL_MEDIA_GROUP:
      return args.valuesArr
        ?.filter((o: OptionValueWithResource) => o.value)
        .map((o: OptionValueWithResource) => (
          <span key={o.value as string}>
            <OpenLinkInNewTab url={o.value as string} title={o.value as string}>
              {/* check is added here but for social media group resource will always be available */}
              {o.resource ? <DisplayIcon iconUrl={o.resource} /> : o.value}
            </OpenLinkInNewTab>
          </span>
        ));
    case FieldRenderType.EMAIL:
      return <EmailLinkClient email={args.value?.value as string} />;
    case FieldRenderType.SECTION_SEPARATOR:
      return <hr />;
    case FieldRenderType.PHONE:
      return args.value?.value;
  }
}

function renderOptionsWithIcons(valuesArr?: Array<OptionValueWithResource>) {
  return (
    <div className="multiSelect--wrapper">
      {valuesArr?.map((o: OptionValueWithResource) => (
        <RenderOptionWithIcon key={o.value?.toString()} option={o} isMultiSelect />
      ))}
    </div>
  );
}

// todo:Ankush need to make classnames generic and not specific to multi-select only
function RenderOptionWithIcon(props: { option: OptionValueWithResource; isMultiSelect?: boolean }) {
  return (
    <div className="optionItem">
      {props.option.resource ? (
        <div className="optionItem_img">
          <DisplayIcon iconUrl={props.option.resource} />
        </div>
      ) : (
        props.isMultiSelect && (
          <span className="optionItem_bullet">
            <span className="optionItem_bullet_circle"></span>
          </span>
        )
      )}
      <span className="optionItem_label">{props.option.value}</span>
    </div>
  );
}
